/**
 * BasicPlanBanner — informa a los tenants en plan Basic de las áreas que
 * NO tienen desbloqueadas y les invita a subir a Pro. Se pinta encima del
 * Dashboard (debajo del TrialBanner). Se oculta:
 *   - Para master/admin (bypasean el gating).
 *   - Para cualquier plan que no sea `basic`.
 *
 * El usuario puede descartar el banner por sesión con la ✕ (localStorage).
 */
import { useState, useEffect } from "react";
import { AlertTriangle, ArrowRight, X } from "lucide-react";
import { Link } from "react-router-dom";

const LOCKED = [
  { key: "inventory",   labelEs: "Inventario",    labelEn: "Inventory" },
  { key: "albaranes",   labelEs: "Albaranes",     labelEn: "Delivery notes" },
  { key: "budget",      labelEs: "Presupuesto",   labelEn: "Budget" },
  { key: "client_debts", labelEs: "Deudas clientes", labelEn: "Client debts" },
  { key: "treasury",    labelEs: "Tesorería",     labelEn: "Treasury" },
];

const DISMISS_KEY = "chefsync.basic-banner-dismissed";

export default function BasicPlanBanner({ user, lang }) {
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; }
    catch { return false; }
  });

  // Reset dismissal when the user changes plan (upgrades).
  useEffect(() => {
    if (user?.plan !== "basic") {
      try { sessionStorage.removeItem(DISMISS_KEY); } catch { /* noop */ }
    }
  }, [user?.plan]);

  if (!user) return null;
  if (user.role === "master" || user.role === "admin") return null;
  if (user.plan !== "basic") return null;
  if (hidden) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
    setHidden(true);
  };

  const lockedList = LOCKED.map((f) => (lang === "es" ? f.labelEs : f.labelEn));

  return (
    <div
      className="rounded-lg border border-[var(--border-color)] bg-[var(--surface)] p-4 flex items-start gap-3"
      data-testid="basic-plan-banner"
    >
      <div className="shrink-0 mt-0.5">
        <AlertTriangle size={18} className="text-amber-600" />
      </div>
      <div className="flex-1 space-y-1">
        <p className="text-sm font-medium text-[var(--text-primary)]">
          {lang === "es"
            ? "Tu plan Basic tiene 5 áreas bloqueadas"
            : "Your Basic plan has 5 locked areas"}
        </p>
        <p className="text-sm text-[var(--text-secondary)]">
          {lang === "es"
            ? `No tienes acceso a: ${lockedList.join(" · ")}. Sube a Pro para desbloquearlas todas.`
            : `You cannot access: ${lockedList.join(" · ")}. Upgrade to Pro to unlock them all.`}
        </p>
        <Link
          to="/settings"
          data-testid="basic-plan-banner-upgrade-link"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--primary)] hover:underline mt-1"
        >
          {lang === "es" ? "Ver planes" : "See plans"}
          <ArrowRight size={14} />
        </Link>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label={lang === "es" ? "Descartar aviso" : "Dismiss notice"}
        data-testid="basic-plan-banner-close"
        className="shrink-0 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
      >
        <X size={16} />
      </button>
    </div>
  );
}
